import React, { useContext, useEffect, useState } from 'react';
import {
  CONNECT,
  GOT_TOURNAMENTS,
  LOBBY_DATA_RECEIVED,
  LOBBY_REFRESH_REQUESTED,
  REGISTER_SOCKET
} from 'constants/eventConstants';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import { LobbyProvider } from 'contexts/LobbyContext';
import SplashScreenContext from 'contexts/SplashScreenContext';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useActivityStripDispatch } from 'hooks/useActivityStripDispatch';
import {
  hasRenderableLobbyData,
  setLobbyReady,
  setSitAndGoList,
  setTournamentList
} from 'reducers/lobby';
import { getLobby } from 'services/lobbyService';
import { createTrailingRefresh, withJitter } from 'services/trailingRefresh';
import store from 'store';
import { decompressData } from 'utils/functions';

import ForcePortrait from 'components/ForcePortrait/ForcePortrait';
import SaloonLobby from 'components/SaloonLobby';

import { dispatchLobbyCashGames } from './lobbyCashGames';

/**
 * Guest (not-logged-in) lobby. A deliberately thin container that reuses the
 * exact same presentational lobby (`SaloonLobby`) and cash-game ingestion
 * (`dispatchLobbyCashGames`) as the member `Lobby`, minus every player-specific
 * concern: no token/login, no geolocation gate, no player-games / tournament
 * detail / seated-table auto-subscribe. The engine accepts the guest socket
 * (auth `{ guest: true }`) and pushes the same `lobby:v1:read:response` /
 * `gotTournamentH5` to the lobby room. Table/tournament CTAs become
 * "Sign up to play" (see SaloonLobbyContainer guest handling).
 */
const GuestLobby: React.FC = () => {
  const dispatch = useAppDispatch();
  const { socket, setGameType } = useContext(SocketContext);
  const lobbyReady = useAppSelector((state) => state.lobby.isLobbyReady);
  const { hasFilter, sortingField, sortingDirection } = useAppSelector((state) => state.lobby);
  const { showSplash, hideSplash } = useContext(SplashScreenContext);

  // Never trap a guest on the splash. If the lobby snapshot hasn't arrived
  // within a short grace period (e.g. the engine isn't accepting guests yet),
  // render the lobby shell anyway so the Log in / Sign up CTAs are reachable.
  const [graceElapsed, setGraceElapsed] = useState(false);

  useEffect(() => {
    setGameType('lobby');
    // Only blank when there is nothing to re-show — see hasRenderableLobbyData.
    // A guest returning from an observed table keeps the lobby they already
    // had instead of dropping to the flat loading fill for a push cycle.
    if (!hasRenderableLobbyData(store.getState().lobby)) dispatch(setLobbyReady(false));
    const t = setTimeout(() => setGraceElapsed(true), 3000);
    return () => clearTimeout(t);
  }, [dispatch, setGameType]);

  useEffect(() => {
    if (!socket) return;

    if (socket.connected) {
      socket.emit(REGISTER_SOCKET, { page: 'lobby' });
    }
    socket.on(CONNECT, () => socket.emit(REGISTER_SOCKET, { page: 'lobby' }));

    const handleLobbyData = (receivedCashGames: any) => {
      const data = decompressData(receivedCashGames);
      if (data !== null && data !== undefined) {
        dispatchLobbyCashGames(data, dispatch, { hasFilter, sortingField, sortingDirection });
        dispatch(setLobbyReady(true));
      }
    };
    socket.on(LOBBY_DATA_RECEIVED, handleLobbyData);

    const abortController = new AbortController();
    const refreshLobby = createTrailingRefresh(
      () =>
        getLobby(abortController.signal)
          .then(handleLobbyData)
          .catch((error) => {
            if (error?.name !== 'AbortError') {
              // eslint-disable-next-line no-console
              console.error('[GuestLobby] REST refresh failed:', error);
            }
          }),
      abortController.signal
    );
    // Jittered: this signal reaches every lobby client at once, and without a
    // spread they'd all call refreshLobby in the same instant (see withJitter).
    const jitteredRefresh = withJitter(() => void refreshLobby());
    socket.on(LOBBY_REFRESH_REQUESTED, jitteredRefresh.trigger);
    void refreshLobby();

    // Named so the cleanup removes only this listener (see Lobby.tsx, EN-11109).
    const handleTournaments = (raw: any) => {
      const data = decompressData(raw);
      // A payload without a response list is ignored, as SitGoResultWatcher does.
      if (!Array.isArray(data?.response)) return;
      // A null entry is skipped, not read.
      dispatch(setSitAndGoList(data.response.filter((row: any) => row?.tabletype === 't')));
      dispatch(setTournamentList(data.response.filter((row: any) => row?.tabletype === 'm')));
    };
    socket.on(GOT_TOURNAMENTS, handleTournaments);

    return () => {
      abortController.abort();
      jitteredRefresh.cancel();
      socket.off(CONNECT);
      socket.off(LOBBY_DATA_RECEIVED);
      socket.off(LOBBY_REFRESH_REQUESTED);
      socket.off(GOT_TOURNAMENTS, handleTournaments);
    };
  }, [socket, dispatch, hasFilter, sortingField, sortingDirection]);

  // Activity strip (Tables / Players / In Play): polled from its own public
  // CDN document (see useActivityStripDispatch/useActivityStripFeed) — no
  // socket connection needed, which matters here since a guest may not have
  // one yet.
  useActivityStripDispatch();

  // No geolocation gate for guests. Briefly show the shared splash while the
  // first snapshot loads, but fall through to the lobby shell after the grace
  // period so a guest is never stuck (e.g. when the engine rejects guest
  // sockets).
  const isLoadingFirstSnapshot = !graceElapsed && (!socket?.connected || !lobbyReady);
  useEffect(() => {
    if (!isLoadingFirstSnapshot) return undefined;
    showSplash('guestLobbyLoading');
    return () => hideSplash('guestLobbyLoading');
  }, [isLoadingFirstSnapshot, showSplash, hideSplash]);

  if (isLoadingFirstSnapshot) {
    return null;
  }

  return (
    <LobbyProvider>
      <ForcePortrait>
        <SaloonLobby />
      </ForcePortrait>
    </LobbyProvider>
  );
};

export default GuestLobby;
